import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Spinner,
  Badge,
  Tabs,
  Tab,
  Table,
  ButtonGroup,
  Dropdown
} from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import Swal from 'sweetalert2';

// Components
import StatusBadge from './components/StatusBadge';

// Services
import MarketplaceServices from './services/MarketplaceServices';

// Styles
import './marketplace.css';
import './MarketplacePublicationDetails.modern.css';

const MarketplacePublicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Load publication data
  useEffect(() => {
    loadPublication();
  }, [id]);

  const loadPublication = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MarketplaceServices.getPublication(id);
      
      // Verificar se a resposta segue a nova estrutura da API
      const publicationData = response.data?.status === 'success' 
        ? response.data.data 
        : response.data;
      
      const formattedPublication = MarketplaceServices.formatPublicationData(publicationData);
      setPublication(formattedPublication);

    } catch (error) {
      console.error('Erro ao carregar publicação:', error);
      setError('Erro ao carregar dados da publicação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Handle action methods (same as in MarketplacePublications)
  const handlePublish = async () => {
    const result = await Swal.fire({
      title: 'Solicitar Publicação',
      text: 'Deseja solicitar a publicação deste agente no marketplace?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, solicitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);
      await MarketplaceServices.publishPublication(id);
      
      Swal.fire({
        icon: 'success',
        title: 'Solicitação Enviada',
        text: 'Sua solicitação de publicação foi enviada para análise.',
        timer: 3000,
        showConfirmButton: false
      });

      loadPublication();
    } catch (error) {
      console.error('Erro ao solicitar publicação:', error);
      
      let errorMessage = 'Erro ao solicitar publicação.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    const result = await Swal.fire({
      title: 'Suspender Publicação',
      text: 'Deseja suspender temporariamente esta publicação?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, suspender',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ffc107'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);
      await MarketplaceServices.suspendPublication(id);
      
      Swal.fire({
        icon: 'success',
        title: 'Publicação Suspensa',
        text: 'A publicação foi suspensa temporariamente.',
        timer: 3000,
        showConfirmButton: false
      });

      loadPublication();
    } catch (error) {
      console.error('Erro ao suspender publicação:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao suspender publicação.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async () => {
    const result = await Swal.fire({
      title: 'Remover Publicação',
      html: `
        <p>Deseja remover permanentemente esta publicação?</p>
        <p class="small text-muted">Esta ação não pode ser desfeita.</p>
      `,
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);
      await MarketplaceServices.removePublication(id);
      
      Swal.fire({
        icon: 'success',
        title: 'Publicação Removida',
        text: 'A publicação foi removida permanentemente.',
        timer: 3000,
        showConfirmButton: false
      }).then(() => {
        navigate('/marketplace');
      });

    } catch (error) {
      console.error('Erro ao remover publicação:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao remover publicação.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Utility functions
  const formatPrice = (price) => {
    if (!price || price === 0) return 'Gratuito';
    return `R$ ${parseFloat(price).toFixed(2).replace('.', ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStats = (count) => {
    if (!count || count === 0) return '0';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>Carregando publicação...</h5>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error || !publication) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>
            <i className="las la-exclamation-triangle me-2" />
            Erro
          </Alert.Heading>
          {error || 'Publicação não encontrada.'}
          <hr />
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-danger" 
              as={Link} 
              to="/marketplace"
            >
              Voltar à Lista
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="mpd-marketplace-details-page">
      {/* Hero Section */}
      <div className="mpd-publication-hero">
        <div className="mpd-publication-banner">
          {publication.banner_url ? (
            <img
              src={publication.banner_url}
              alt={publication.agent_name}
              className="mpd-publication-banner-image"
            />
          ) : (
            <div className="mpd-publication-banner-placeholder" />
          )}

          {/* Back Button */}
          <Link
            to="/marketplace"
            className="mpd-hero-back-button"
            title="Voltar"
          >
            <i className="las la-arrow-left" />
          </Link>
        </div>

        {/* Hero Content */}
        <div className="mpd-publication-hero-content">
          <h1 className="mpd-hero-title">{publication.agent_name}</h1>

          {publication.agent_description && (
            <p className="mpd-hero-subtitle">{publication.agent_description}</p>
          )}

          <div className="mpd-hero-meta">
            <div className="mpd-hero-meta-item">
              <i className="las la-tag" />
              <span>ID: {publication.marketplace_product_id}</span>
            </div>

            {publication.agent_job && (
              <div className="mpd-hero-meta-item">
                <i className="las la-briefcase" />
                <span>{publication.agent_job}</span>
              </div>
            )}

            <div className="mpd-hero-meta-item">
              <i className="las la-calendar" />
              <span>Criado em {formatDate(publication.created_at)}</span>
            </div>

            <div className="mpd-hero-meta-item">
              <StatusBadge status={publication.status} />
            </div>
          </div>
        </div>
      </div>

      <div className="mpd-content-container">
        <div className="mpd-main-content">
          {/* Rejection Notice */}
          {publication.status === "rejected" &&
            publication.rejection_reason && (
              <Alert variant="danger" className="m-4">
                <Alert.Heading>
                  <i className="las la-exclamation-triangle me-2" />
                  Publicação Rejeitada
                </Alert.Heading>
                <strong>Motivo:</strong> {publication.rejection_reason}
                <hr />
                <div className="d-flex justify-content-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    as={Link}
                    to={`/marketplace/edit/${id}`}
                  >
                    <i className="las la-edit me-1" />
                    Corrigir e Reenviar
                  </Button>
                </div>
              </Alert>
            )}

          {/* Modern Tabs */}
          <div className="mpd-modern-tabs">
            <div className="mpd-modern-tabs-nav">
              <div className="mpd-modern-tabs-list">
                <div
                  className={`mpd-modern-tab ${
                    activeTab === "overview" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("overview")}
                >
                  <div className="mpd-tab-icon">
                    <i className="las la-info-circle" />
                  </div>
                  <div className="mpd-tab-content">
                    <div className="mpd-tab-title">Visão Geral</div>
                  </div>
                </div>

                <div
                  className={`mpd-modern-tab ${
                    activeTab === "features" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("features")}
                >
                  <div className="mpd-tab-icon">
                    <i className="las la-cogs" />
                  </div>
                  <div className="mpd-tab-content">
                    <div className="mpd-tab-title">Funcionalidades</div>
                  </div>
                </div>

                <div
                  className={`mpd-modern-tab ${
                    activeTab === "technical" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("technical")}
                >
                  <div className="mpd-tab-icon">
                    <i className="las la-code" />
                  </div>
                  <div className="mpd-tab-content">
                    <div className="mpd-tab-title">Técnico</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Tab Content */}
          {activeTab === "overview" && (
            <div className="mpd-content-section">
              <div className="mpd-section-header">
                <div className="mpd-section-icon">
                  <i className="las la-info-circle" />
                </div>
                <div>
                  <h3 className="mpd-section-title">Visão Geral</h3>
                  <p className="mpd-section-subtitle">
                    Informações principais e descrição do agente
                  </p>
                </div>
              </div>

              {/* Agent Description */}
              {publication.agent_description && (
                <div className="mb-4">
                  <div className="mpd-description-content">
                    <p>{publication.agent_description}</p>
                  </div>
                </div>
              )}

              {/* Full Description if different from agent_description */}
              {publication.description &&
                publication.description !== publication.agent_description && (
                  <div className="mb-4">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: publication.description,
                      }}
                      className="marketplace-description-content"
                    />
                  </div>
                )}

              {/* Instructions Content */}
              {publication.instructions && (
                <div className="mb-4">
                  {publication.instructions.agent_configuration
                    ?.setup_requirements && (
                    <div className="mb-3">
                      <h5>Requisitos de Configuração</h5>
                      <p>
                        {
                          publication.instructions.agent_configuration
                            .setup_requirements
                        }
                      </p>
                    </div>
                  )}

                  {publication.instructions.agent_configuration
                    ?.configuration_steps && (
                    <div className="mb-3">
                      <h5>Passos de Configuração</h5>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            publication.instructions.agent_configuration
                              .configuration_steps,
                        }}
                        className="marketplace-description-content"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Video */}
              {publication.video_url && (
                <div className="mpd-video-container">
                  <ReactPlayer
                    src={publication.video_url}
                    controls
                    width="100%"
                    height="400px"
                    config={{
                      youtube: {
                        playerVars: {
                          showinfo: 1,
                          rel: 0,
                          modestbranding: 1,
                        },
                      },
                    }}
                  />
                </div>
              )}

              {/* Gallery Images */}
              {publication.gallery_images &&
                publication.gallery_images.length > 0 && (
                  <div className="mpd-gallery-container">
                    <h5>Galeria de Imagens</h5>
                    <div className="mpd-gallery-grid">
                      {publication.gallery_images.map((image, index) => (
                        <div key={index} className="mpd-gallery-item">
                          <img
                            src={image}
                            alt={`Galeria ${index + 1}`}
                            className="mpd-gallery-image"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {activeTab === "features" && (
            <div className="mpd-content-section">
              <div className="mpd-section-header">
                <div className="mpd-section-icon">
                  <i className="las la-cogs" />
                </div>
                <div>
                  <h3 className="mpd-section-title">Funcionalidades</h3>
                  <p className="mpd-section-subtitle">
                    Recursos e capacidades do agente
                  </p>
                </div>
              </div>

              {/* Customization Options */}
              {publication.instructions?.agent_configuration
                ?.customization_options && (
                <div className="mb-4">
                  <h5>Opções de Personalização</h5>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        publication.instructions.agent_configuration
                          .customization_options,
                    }}
                    className="marketplace-description-content"
                  />
                </div>
              )}

              {/* Capabilities */}
              {publication.capabilities &&
                publication.capabilities.length > 0 && (
                  <div className="mpd-capabilities-list">
                    {publication.capabilities.map((capability, index) => (
                      <div key={index} className="mpd-capability-item">
                        <div className="mpd-capability-check">
                          <i className="las la-check-circle" />
                        </div>
                        <p className="mpd-capability-text">{capability}</p>
                      </div>
                    ))}
                  </div>
                )}

              {/* Features Grid */}
              {publication.features && publication.features.length > 0 && (
                <div className="mpd-features-grid">
                  {publication.features.map((feature, index) => (
                    <div key={index} className="mpd-feature-card">
                      <div className="mpd-feature-header">
                        {feature.icon && (
                          <div className="mpd-feature-icon">
                            <i className={feature.icon} />
                          </div>
                        )}
                        <h6 className="mpd-feature-title">{feature.name}</h6>
                      </div>
                      {feature.description && (
                        <p className="mpd-feature-description">
                          {feature.description}
                        </p>
                      )}
                      {feature.is_premium && (
                        <Badge bg="warning" className="mt-2">
                          Premium
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "technical" && (
            <div className="mpd-content-section">
              <div className="mpd-section-header">
                <div className="mpd-section-icon">
                  <i className="las la-code" />
                </div>
                <div>
                  <h3 className="mpd-section-title">Especificações Técnicas</h3>
                  <p className="mpd-section-subtitle">
                    Detalhes técnicos e configurações
                  </p>
                </div>
              </div>

              {/* Platform Access Info */}
              {publication.instructions?.platform_access && (
                <div className="mb-4">
                  <h5>Acesso à Plataforma</h5>
                  <div className="mpd-platform-info">
                    <p>
                      <strong>Plataforma:</strong>{" "}
                      {publication.instructions.platform_access.platform_name}
                    </p>
                    <p>
                      <strong>URL:</strong>{" "}
                      <a
                        href={
                          publication.instructions.platform_access.platform_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {publication.instructions.platform_access.platform_url}
                      </a>
                    </p>
                    <p>
                      <strong>Descrição:</strong>{" "}
                      {
                        publication.instructions.platform_access
                          .platform_description
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Support Contact */}
              {publication.instructions?.support_contact && (
                <div className="mb-4">
                  <h5>Contato de Suporte</h5>
                  <div className="mpd-support-info">
                    <p>
                      <strong>Contato:</strong>{" "}
                      {publication.instructions.support_contact.contact_name}
                    </p>
                    <p>
                      <strong>Tipo:</strong>{" "}
                      {publication.instructions.support_contact.contact_type}
                    </p>
                    <p>
                      <strong>Valor:</strong>{" "}
                      {publication.instructions.support_contact.contact_value}
                    </p>
                    <p>
                      <strong>Horários:</strong>{" "}
                      {publication.instructions.support_contact.support_hours}
                    </p>
                  </div>
                </div>
              )}

              {/* Access Credentials */}
              {publication.instructions?.access_credentials && (
                <div className="mb-4">
                  <h5>Credenciais de Acesso</h5>
                  <div className="mpd-credentials-info">
                    <p>
                      <strong>Tipo:</strong>{" "}
                      {
                        publication.instructions.access_credentials
                          .credential_type
                      }
                    </p>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          publication.instructions.access_credentials
                            .instructions,
                      }}
                      className="marketplace-description-content"
                    />
                    {publication.instructions.access_credentials
                      .additional_info && (
                      <p>
                        <strong>Informações adicionais:</strong>{" "}
                        {
                          publication.instructions.access_credentials
                            .additional_info
                        }
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Technical Specs Object */}
              {publication.technical_specs &&
                typeof publication.technical_specs === "string" &&
                publication.technical_specs !== "{}" && (
                  <div className="mb-4">
                    <h5>Especificações Técnicas</h5>
                    <div className="mpd-tech-specs">
                      <pre>{publication.technical_specs}</pre>
                    </div>
                  </div>
                )}

              {/* Platform Support */}
              {publication.platform && publication.platform.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted text-uppercase fw-bold mb-2">
                    Plataformas Suportadas
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {publication.platform.map((platform, index) => (
                      <Badge key={index} bg="secondary" className="px-3 py-2">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Info */}
              {publication.category && (
                <div className="mb-4">
                  <h6 className="text-muted text-uppercase fw-bold mb-2">
                    Categoria
                  </h6>
                  <Badge bg="primary" className="px-3 py-2">
                  {publication.category_name}
                  </Badge>
                </div>
              )}

              {/* Tags */}
              {publication.tags && publication.tags.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted text-uppercase fw-bold mb-2">
                    Tags
                  </h6>
                  <div className="d-flex flex-wrap gap-1">
                    {publication.tags.map((tag, index) => (
                      <Badge key={index} bg="light" text="dark">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mpd-publication-sidebar">
          {/* Stats Card */}
          <div className="mpd-sidebar-card">
            <div className="mpd-sidebar-card-header">
              <h6 className="mpd-sidebar-card-title">
                <div className="mpd-sidebar-card-icon">
                  <i className="las la-chart-bar" />
                </div>
                Estatísticas
              </h6>
            </div>

            <div className="mpd-sidebar-card-body">
              <div className="mpd-stats-grid">
                <div className="mpd-stat-item">
                  <div className="mpd-stat-value text-primary">
                    {formatStats(publication.download_count)}
                  </div>
                  <div className="mpd-stat-label">Downloads</div>
                </div>

                <div className="mpd-stat-item">
                  <div className="mpd-stat-value text-success">
                    {formatStats(publication.view_count)}
                  </div>
                  <div className="mpd-stat-label">Visualizações</div>
                </div>

                <div className="mpd-stat-item">
                  <div className="mpd-stat-value text-warning">
                    {publication.rating ? publication.rating.toFixed(1) : "0.0"}
                  </div>
                  <div className="mpd-stat-label">Avaliação</div>
                </div>
              </div>

              {publication.rating && publication.rating > 0 ? (
                <div className="mpd-rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`las la-star ${
                        star <= publication.rating
                          ? "text-warning"
                          : "text-muted"
                      }`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Price Card */}
          <div className="mpd-sidebar-card">
            <div className="mpd-sidebar-card-header">
              <h6 className="mpd-sidebar-card-title">
                <div className="mpd-sidebar-card-icon">
                  <i className="las la-dollar-sign" />
                </div>
                Preço
              </h6>
            </div>

            <div className="mpd-sidebar-card-body">
              <div className="mpd-price-display">
                {publication.pricing_info?.discount_percentage &&
                publication.pricing_info?.discount_percentage > 0 ? (
                  <div>
                    <div className="mpd-price-value">
                      {formatPrice(publication.pricing_info.price)}
                      <small>/mês</small>
                    </div>
                    <div className="mpd-price-original">
                      {formatPrice(
                        publication.pricing_info.price /
                          (1 -
                            publication.pricing_info.discount_percentage / 100)
                      )}
                    </div>
                    <div className="mpd-price-badge">
                      {publication.pricing_info.discount_percentage}% OFF
                    </div>
                  </div>
                ) : (
                  <div className="mpd-price-value">
                    {formatPrice(
                      publication.pricing_info?.price || publication.price
                    )}
                    <small>/mês</small>
                  </div>
                )}

                <div className="mpd-price-details mt-3">
                  {/* <p className="mb-1">
                    <strong>Moeda:</strong> {publication.pricing_info?.currency || 'BRL'}
                  </p> */}
                  {/* <p className="mb-1">
                    <strong>Tipo:</strong> {publication.pricing_info?.billing_type === 'one_time' ? 'Pagamento único' : 'Recorrente'}
                  </p> */}
                  {publication.pricing_info?.trial_period_days && (
                    <Badge bg="info" className="mt-2">
                      {publication.pricing_info.trial_period_days} dias de teste
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Publication Info Card */}
          <div className="mpd-sidebar-card">
            <div className="mpd-sidebar-card-header">
              <h6 className="mpd-sidebar-card-title">
                <div className="mpd-sidebar-card-icon">
                  <i className="las la-info-circle" />
                </div>
                Informações da Publicação
              </h6>
            </div>

            <div className="">
              <Table size="sm" className="mpd-info-table mb-0">
                <tbody>
                  <tr>
                    <td className="fw-bold">Status:</td>
                    <td>
                      <StatusBadge status={publication.status} />
                      {publication.status_display && (
                        <small className="text-muted d-block">
                          {publication.status_display}
                        </small>
                      )}
                    </td>
                  </tr>

                  {/*  <tr>
                    <td className="fw-bold">Agente ID:</td>
                    <td>
                      <code>{publication.agent_id}</code>
                    </td>
                  </tr> */}

                  <tr>
                    <td className="fw-bold">Slug:</td>
                    <td>
                      <code>{publication.agent_slug}</code>
                    </td>
                  </tr>

                  <tr>
                    <td className="fw-bold">Criado em:</td>
                    <td>{formatDate(publication.created_at)}</td>
                  </tr>

                  {publication.updated_at !== publication.created_at && (
                    <tr>
                      <td className="fw-bold">Atualizado em:</td>
                      <td>{formatDate(publication.updated_at)}</td>
                    </tr>
                  )}

                  {publication.published_at && (
                    <tr>
                      <td className="fw-bold">Publicado em:</td>
                      <td>{formatDate(publication.published_at)}</td>
                    </tr>
                  )}

                  <tr>
                    <td className="fw-bold">ID do Produto:</td>
                    <td>
                      <code>{publication.marketplace_product_id}</code>
                    </td>
                  </tr>

                  {publication.seller_profile && (
                    <tr>
                      <td className="fw-bold">Perfil do Vendedor:</td>
                      <td>{publication.seller_profile}</td>
                    </tr>
                  )}

                  {/*  <tr>
                    <td className="fw-bold">Pontuação:</td>
                    <td>{publication.popularity_score || 0}</td>
                  </tr> */}

                  <tr>
                    <td className="fw-bold">Download:</td>
                    <td>{publication.for_download ? "Sim" : "Não"}</td>
                  </tr>

                  <tr>
                    <td className="fw-bold">Em Destaque:</td>
                    <td>{publication.featured ? "Sim" : "Não"}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="mpd-sidebar-card">
            <div className="mpd-sidebar-card-header">
              <h6 className="mpd-sidebar-card-title">
                <div className="mpd-sidebar-card-icon">
                  <i className="las la-bolt" />
                </div>
                Ações Rápidas
              </h6>
            </div>

            <div className="mpd-sidebar-card-body">
              <div className="mpd-quick-actions">
                {publication.canEdit && (
                  <Button
                    className="mpd-action-button outline"
                    as={Link}
                    to={`/marketplace/edit/${id}`}
                    disabled={actionLoading}
                  >
                    <i className="las la-edit" />
                    Editar Publicação
                  </Button>
                )}

                {publication.canPublish && (
                  <Button
                    className="mpd-action-button primary"
                    onClick={handlePublish}
                    disabled={actionLoading}
                  >
                    <i className="las la-upload" />
                    Solicitar Publicação
                  </Button>
                )}

                {publication.canSuspend && (
                  <Button
                    className="mpd-action-button outline"
                    onClick={handleSuspend}
                    disabled={actionLoading}
                  >
                    <i className="las la-pause" />
                    Suspender
                  </Button>
                )}

                {publication.marketplace_url && (
                  <Button
                    className="mpd-action-button outline"
                    href={publication.marketplace_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="las la-external-link-alt" />
                    Ver no Marketplace
                  </Button>
                )}

                {publication.canRemove && (
                  <button
                    className="mpd-action-button outline"
                    onClick={handleRemove}
                    disabled={actionLoading}
                    style={{
                      borderColor: "var(--danger)",
                      color: "var(--danger)",
                    }}
                  >
                    <i className="las la-trash" />
                    Remover
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePublicationDetails;